import AIFormReferencesList, {
  AIFormReferencesListProps,
} from "@/content/ai/AIFormReferencesList"
import { GlobalID } from "@/relay/RelayTypes"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import AIInput from "@components/ai/AIInput"
import WithEntitlement from "@components/entitlement/WithEntitlement"
import Form from "@components/form/Form"
import { DiscoButton, DiscoFormControl, DiscoSection } from "@disco-ui"
import useHasEntitlement from "@utils/hook/useHasEntitlement"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { useState } from "react"

export type GenerateContentWithAISubmitProps = {
  prompt: string
  referenceUrl?: string
  referenceEmbeddingSourceIds?: GlobalID[]
  referenceModuleContentUsageId?: GlobalID
}

export interface GenerateContentWithAIFormProps extends TestIDProps {
  onSubmit: ({
    prompt,
    referenceUrl,
    referenceEmbeddingSourceIds,
    referenceModuleContentUsageId,
  }: GenerateContentWithAISubmitProps) => Promise<void> | void
  hideReferenceKinds?: AIFormReferencesListProps["hideReferenceKinds"]
  moduleContentUsageId?: GlobalID
}

function GenerateContentWithAIForm({
  onSubmit,
  testid = "GenerateContentWithAIForm",
  hideReferenceKinds,
  moduleContentUsageId,
}: GenerateContentWithAIFormProps) {
  const hasEntitlement = useHasEntitlement("ai_content_generation")
  const [prompt, setPrompt] = useState<string>("")
  const [referenceUrl, setReferenceUrl] = useState<string>("")
  const [assetEmbeddingSourceIds, setAssetEmbeddingSourceIds] = useState<GlobalID[]>([])
  const [contentEmbeddingSourceIds, setContentEmbeddingSourceIds] = useState<GlobalID[]>(
    []
  )
  const [trainingDataEmbeddingSourceIds, setTrainingDataEmbeddingSourceIds] = useState<
    GlobalID[]
  >([])
  const [shouldReferenceContentModule, setShouldReferenceContentModule] =
    useState<boolean>(false)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const classes = useStyles()

  return (
    <DiscoSection className={classes.container}>
      <Form testid={`${testid}.form`} errorInfo={null}>
        <DiscoFormControl entitlement={"ai_content_generation"}>
          <AIInput
            testid={`${testid}.prompt-input`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={!hasEntitlement}
            placeholder={"Describe a prompt for the content you want to create"}
            showBadge={false}
          />
        </DiscoFormControl>
        <DiscoSection groovyDepths={"insideCard"}>
          <DiscoFormControl label={"References"} optional>
            <AIFormReferencesList
              hideReferenceKinds={hideReferenceKinds}
              referenceUrl={referenceUrl}
              onReferenceUrlChange={(v) => setReferenceUrl(v)}
              assetEmbeddingSourceIds={assetEmbeddingSourceIds}
              onAssetEmbeddingSourceIdsSelect={setAssetEmbeddingSourceIds}
              contentEmbeddingSourceIds={contentEmbeddingSourceIds}
              onContentEmbeddingSourceIdsSelect={setContentEmbeddingSourceIds}
              trainingDataEmbeddingSourceIds={trainingDataEmbeddingSourceIds}
              onTrainingDataEmbeddingSourceIdsSelect={setTrainingDataEmbeddingSourceIds}
              moduleContentUsageId={moduleContentUsageId}
              shouldReferenceContentModule={shouldReferenceContentModule}
              setShouldReferenceContentModule={setShouldReferenceContentModule}
            />
          </DiscoFormControl>
        </DiscoSection>
        <div className={classes.buttonContainer}>
          <WithEntitlement entitlement={"ai_image_generation"}>
            {({ hasEntitlement: hasEntitlementInner }) => (
              <DiscoButton
                leftIcon={"stars"}
                onClick={handleSubmit}
                testid={`${testid}.generate`}
                disabled={!hasEntitlementInner}
                className={classes.generateButton}
                shouldDisplaySpinner={isSubmitting}
              >
                {"Generate"}
              </DiscoButton>
            )}
          </WithEntitlement>
        </div>
      </Form>
    </DiscoSection>
  )

  async function handleSubmit() {
    setIsSubmitting(true)
    await onSubmit({
      prompt,
      referenceUrl,
      referenceEmbeddingSourceIds: [
        ...assetEmbeddingSourceIds,
        ...contentEmbeddingSourceIds,
        ...trainingDataEmbeddingSourceIds,
      ],
      referenceModuleContentUsageId: shouldReferenceContentModule
        ? moduleContentUsageId
        : undefined,
    })
    setIsSubmitting(false)
  }
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    background:
      theme.palette.type === "dark"
        ? theme.palette.aiGradient.bluePurpleDark
        : theme.palette.aiGradient.aiLight,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: theme.spacing(2),
  },
  generateButton: {
    background: theme.palette.aiGradient.aiDark,
  },
}))

export default observer(GenerateContentWithAIForm)
